/**
 * 审批参数的**多路解析**（纯函数，便于单测）。
 *
 * 为什么单独成文件：审批卡能不能弹出来，取决于我们能不能从 `approval-request`
 * 事件里拿到 `actions`。而这一步此前只认"服务端解析好的 `argumentsParsed.actions`"，
 * 只要有半点形状不符（解析结果为空对象、参数还没配上、只给了裸数组、
 * `actions` 自身是 JSON 字符串……）就会得到**空列表** —— 审批卡弹不出来，
 * 调用方只好"自动拒绝"，于是用户屏幕上什么都没有，模型却被告知
 * `the user rejected tool "execute"`。生产上偶发、不可复现的"用户没操作却被拒绝"就是这个。
 *
 * 所以这里把"取 actions"做成逐路兜底：服务端解析结果 → 原始参数文本 → 深层解包，
 * 任何一路拿到非空 actions 就用它。全部失败才由调用方决定怎么收场。
 */

/** 一条 execute 操作（与 ApprovalRequest.batchItems 的构造口径一致） */
export interface ExecuteActionLike {
  action: string;
  target: string;
  targetId?: string;
  data?: Record<string, unknown>;
}

/** 最多解包层数：兼容模型偶发的"JSON 字符串的字符串" */
const MAX_UNWRAP_DEPTH = 4;

/**
 * 宽松解析 JSON 文本：失败返回 `undefined`（**绝不抛异常**）。
 *
 * `argumentsText` 可能是流式未完成的 JSON（写到一半就被审批拦下），
 * `JSON.parse` 必然失败；那种情况只能放弃这一路，而不是让整条审批流程崩掉。
 */
export function parseJsonLoose(text: string): unknown {
  const trimmed = text.trim();
  if (!trimmed) return undefined;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    // "JSON 的字符串"：再解一层（模型双编码时很常见）
    return typeof parsed === 'string' ? parseJsonLoose(parsed) : parsed;
  } catch {
    return undefined;
  }
}

/**
 * 把任意形状的参数归一成操作数组：
 * - `[...]` → 直接用（宽松兜底：模型偶尔直接给数组）
 * - `{ actions: [...] }` → 用 actions
 * - `{ actions: {...} }`（单条未包装）→ 包成一条
 * - `{ action, target, ... }` → 单条操作
 * - `"..."`（JSON 文本）→ 先解析再递归
 */
export function normalizeActions(raw: unknown): ExecuteActionLike[] {
  if (raw === undefined || raw === null) return [];
  if (Array.isArray(raw)) return raw as ExecuteActionLike[];
  if (typeof raw === 'string') {
    const parsed = parseJsonLoose(raw);
    return parsed === undefined ? [] : normalizeActions(parsed);
  }
  if (typeof raw !== 'object') return [];
  const single = raw as { action?: unknown };
  // 单条未包装的操作：`{action:'update', target:'chapter', ...}`
  return typeof single.action === 'string' ? [raw as ExecuteActionLike] : [];
}

/**
 * 从一个参数值里取出 actions（对象 / 数组 / JSON 文本 / JSON 的字符串都吃）。
 *
 * 注意：`actions` 自身也可能是字符串或再包一层对象，所以这里递归解，
 * 但深度有限、且不会对同一个值反复解析（避免解析失败时打转）。
 */
export function extractExecuteActions(value: unknown, depth = 0): ExecuteActionLike[] {
  if (depth > MAX_UNWRAP_DEPTH) return [];
  const parsed = typeof value === 'string' ? parseJsonLoose(value) : value;
  if (parsed === undefined || parsed === null) return [];

  // 裸数组 / 单条操作对象 / `{action,...}`
  const direct = normalizeActions(parsed);
  if (direct.length > 0) return direct;

  if (typeof parsed !== 'object') return [];
  const wrapper = parsed as { actions?: unknown };
  if (wrapper.actions === undefined || wrapper.actions === parsed) return [];
  // `{actions: "..."}` / `{actions: {actions: [...]}}` 这类多包了一层的情况
  return extractExecuteActions(wrapper.actions, depth + 1);
}

/** 把参数值解成对象（数组会被视作 actions 包一层），解不出来返回 undefined */
function asArgsObject(value: unknown): Record<string, unknown> | undefined {
  const parsed = typeof value === 'string' ? parseJsonLoose(value) : value;
  if (Array.isArray(parsed)) return { actions: parsed };
  if (parsed && typeof parsed === 'object') return parsed as Record<string, unknown>;
  return undefined;
}

/**
 * 从一个审批事件（或任何"已解析对象 + 原始文本"的组合）里解析出审批卡需要的参数。
 *
 * 顺序：先信服务端解析结果（它做过深解包），拿不到 actions 再退回原始 `argumentsText`。
 * `description` / `atomic` 用**第一个解成对象**的那一路，这样即便 actions 是从文本里
 * 兜回来的，说明文字也不会丢。
 */
export function resolveApprovalArgs(
  ev: { argumentsParsed?: unknown; argumentsText?: unknown },
): { actions: ExecuteActionLike[]; description?: string; atomic: boolean } {
  const candidates: unknown[] = [];
  if (ev.argumentsParsed !== undefined && ev.argumentsParsed !== null) candidates.push(ev.argumentsParsed);
  if (ev.argumentsText !== undefined && ev.argumentsText !== null) candidates.push(ev.argumentsText);

  let actions: ExecuteActionLike[] = [];
  let args: Record<string, unknown> | undefined;
  for (const candidate of candidates) {
    const obj = asArgsObject(candidate);
    const found = extractExecuteActions(candidate);
    if (found.length > 0) {
      actions = found;
      args = obj ?? args;
      break;
    }
    if (!args) args = obj;
  }
  const description = typeof args?.description === 'string' ? args.description : undefined;
  return { actions, ...(description !== undefined ? { description } : {}), atomic: args?.atomic === true };
}
